import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { all_routes } from "../../router/all_routes";
import { Link, useLocation } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import API_URLS from "../../../core/common/apiUrls";
import DOMPurify from "dompurify";

const HotelBooking: React.FC = () => {
  const routes = all_routes;
  const location = useLocation();

  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumbs = [
    { label: "Hotel Booking Confirmation", active: false, link: routes.home1 },
    { label: "Hotel", active: false },
    { label: "Booking Confirmation", active: true },
  ];

  useEffect(() => {
    const bookingId = location.state?.BookingId;
    const traceId = location.state?.TraceId;

    if (!bookingId) {
      setError("Missing booking information. Please try again.");
      setLoading(false);
      return;
    }

    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const body: any = { BookingId: bookingId };
        if (traceId) body.TraceId = traceId;

        const res = await fetch(API_URLS.HOTELS.BOOKING_DETAIL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!res.ok) throw new Error("Failed to fetch booking details");
        const data = await res.json();

        // Try common shapes, prefer GetBookingDetailResult, else fallback to direct response
        const details =
          data?.GetBookingDetailResult || data?.BookingDetail || data || null;

        setBookingDetails(details);
      } catch (err: any) {
        console.error("Booking details error:", err);
        setError("Unable to fetch booking details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [location.state]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <h4>Fetching booking details...</h4>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5 text-danger">
        <h4>{error}</h4>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="container py-5 text-danger">
        <h4>No booking details found.</h4>
      </div>
    );
  }

  // bookingDetails may already contain Rooms[] (each room has HotelPassenger[]).
  const rooms = bookingDetails.Rooms || bookingDetails.Room || [];

  // compute totals across rooms
  const totals = rooms.reduce(
    (acc: any, r: any) => {
      acc.rooms += 1;
      acc.adults += Number(r?.AdultCount || r?.HotelPassenger?.filter((p: any) => p.PaxType === 1)?.length || 0);
      acc.children += Number(r?.ChildCount || r?.HotelPassenger?.filter((p: any) => p.PaxType === 2)?.length || 0);
      return acc;
    },
    { rooms: 0, adults: 0, children: 0 }
  );

  return (
    <div>
      <Breadcrumb
        title="Hotel Booking Confirmation"
        breadcrumbs={breadcrumbs}
        backgroundClass="breadcrumb-bg-01"
      />

      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card booking-confirmation mb-0">
                <div className="card-body">
                  {/* Hotel Info */}
                  <div className="bg-light-200 border border-light p-3 rounded-2 mb-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                      <div className="d-flex flex-wrap align-items-center booking-hotels">
                        <Link
                          to={routes.hotelDetails}
                          className="avatar avatar-lg me-2"
                        >
                          <ImageWithBasePath
                            src="assets/img/hotels/hotel-06.jpg"
                            alt="hotel"
                            className="img-fluid rounded-circle"
                          />
                        </Link>
                        <div className="booking-details">
                          <h6 className="mb-1">{bookingDetails.HotelName}</h6>
                          <p className="fs-14 text-gray-6">
                            {bookingDetails.AddressLine1 || bookingDetails.Address || bookingDetails?.HotelAddress}
                          </p>
                          <p className="fs-14 text-gray-6">
                            {bookingDetails.City || bookingDetails.CityName}, {bookingDetails.CountryCode || bookingDetails.Country}
                          </p>
                        </div>
                      </div>
                      <div>
                        <span className="badge badge-success status rounded-pill p-2 fs-10 d-flex align-items-center">
                          {bookingDetails.HotelBookingStatus || bookingDetails.Status || "Confirmed"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Rooms Details: show each room separately */}
                  <div className="pb-4 border-bottom mb-4">
                    <h6 className="mb-2">Room Details</h6>
                    {rooms.map((room: any, rIdx: number) => {
                      // common room fields might be in RoomDescription or Name or RoomType
                      const roomDesc =
                        room?.RoomDescription ||
                        (room?.Name ? (Array.isArray(room.Name) ? room.Name.join(", ") : room.Name) : "");
                      const roomRate =
                        (room?.PriceBreakUp && (room.PriceBreakUp.RoomRate || "")) ||
                        (room?.PriceBreakUp?.[0]?.RoomRate || "");
                      const currency =
                        (room?.PriceBreakUp && room.PriceBreakUp.CurrencyCode) ||
                        (room?.PriceBreakUp?.[0]?.CurrencyCode) ||
                        bookingDetails?.Currency ||
                        "";

                      return (
                        <div key={rIdx} className="mb-3">
                          <div className="row g-3">
                            <div className="col-lg-3">
                              <h6 className="fs-14 mb-1">Room Type</h6>
                              <p
                                className="fs-16 text-gray-6"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(roomDesc || "") }}
                              />
                            </div>
                            <div className="col-lg-3">
                              <h6 className="fs-14 mb-1">No of Rooms</h6>
                              <p className="fs-16 text-gray-6">{room?.NoOfRooms || 1}</p>
                            </div>
                            <div className="col-lg-3">
                              <h6 className="fs-14 mb-1">Room Price</h6>
                              <p className="fs-16 text-gray-6">
                                {roomRate} {currency}
                              </p>
                            </div>
                            <div className="col-lg-3">
                              <h6 className="fs-14 mb-1">Guests</h6>
                              <p className="fs-16 text-gray-6">
                                {room?.AdultCount ?? room?.HotelPassenger?.filter((p: any) => p.PaxType === 1).length} Adults,{" "}
                                {room?.ChildCount ?? room?.HotelPassenger?.filter((p: any) => p.PaxType === 2).length} Children
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Booking Info */}
                  <div className="pb-4 border-bottom mb-4">
                    <h6 className="mb-2">Booking Info</h6>
                    <div className="row g-3">
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Booking ID</h6>
                        <p className="fs-16 text-primary">
                          {bookingDetails.BookingId || bookingDetails?.BookingRefNo}
                        </p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Confirmation No</h6>
                        <p className="fs-16 text-gray-6">
                          {bookingDetails.ConfirmationNo || bookingDetails?.BookingRefNo}
                        </p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Check-In</h6>
                        <p className="fs-16 text-gray-6">
                          {bookingDetails.CheckInDate ? new Date(bookingDetails.CheckInDate).toLocaleDateString() : ""}
                        </p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Check-Out</h6>
                        <p className="fs-16 text-gray-6">
                          {bookingDetails.CheckOutDate ? new Date(bookingDetails.CheckOutDate).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Guest Info — show guests per room */}
                  <div className="pb-4 border-bottom mb-4">
                    <h6 className="mb-2">Guest Info</h6>

                    {rooms.map((room: any, rIdx: number) => (
                      <div key={rIdx} className="mb-3">
                        <h6 className="small fw-bold">Room {rIdx + 1}</h6>
                        {Array.isArray(room?.HotelPassenger) && room.HotelPassenger.length > 0 ? (
                          room.HotelPassenger.map((pax: any, idx: number) => (
                            <div key={idx} className="row g-3 mb-2">
                              <div className="col-lg-3">
                                <h6 className="fs-14 mb-1">Name</h6>
                                <p className="fs-16 text-gray-6">
                                  {pax.Title} {pax.FirstName} {pax.LastName}
                                </p>
                              </div>
                              <div className="col-lg-3">
                                <h6 className="fs-14 mb-1">Email</h6>
                                <p className="fs-16 text-gray-6">{pax.Email}</p>
                              </div>
                              <div className="col-lg-3">
                                <h6 className="fs-14 mb-1">Phone</h6>
                                <p className="fs-16 text-gray-6">{pax.Phoneno}</p>
                              </div>
                              <div className="col-lg-3">
                                <h6 className="fs-14 mb-1">PAN</h6>
                                <p className="fs-16 text-gray-6">{pax.PAN || pax.PanNumber || pax.Pan}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted">No passenger data for this room.</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Rate Conditions */}
                  {bookingDetails.RateConditions?.length > 0 && (
                    <div className="pb-4 border-bottom mb-4">
                      <h6 className="mb-2">Rate Conditions</h6>
                      <ul className="list-unstyled small">
                        {bookingDetails.RateConditions.map((cond: string, i: number) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cond) }} />
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cancellation Policy (aggregate across rooms) */}
                  {rooms.some((r: any) => r.CancelPolicies?.length > 0) && (
                    <div className="pb-4 border-bottom mb-4">
                      <h6 className="mb-2">Cancellation Policy</h6>
                      {rooms.map((r: any, ridx: number) =>
                        r.CancelPolicies?.map((p: any, i: number) => (
                          <li key={`${ridx}-${i}`}>
                            📅 {p.FromDate} →{" "}
                            <span className="badge bg-secondary">
                              {p.ChargeType === 2 ? `${p.CancellationCharge}%` : `${p.ChargeType} - ${p.CancellationCharge}`}
                            </span>
                          </li>
                        ))
                      )}
                    </div>
                  )}

                  {/* Payment Info */}
                  <div className="pb-4 border-bottom mb-4">
                    <h6 className="mb-2">Payment Info</h6>
                    <div className="row g-3">
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Invoice No</h6>
                        <p className="fs-16 text-primary">
                          {bookingDetails.InvoiceNo}
                        </p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Invoice Amount</h6>
                        <p className="fs-16 text-gray-6">
                          {bookingDetails.InvoiceAmount}{" "}
                          {rooms?.[0]?.PriceBreakUp?.CurrencyCode || bookingDetails?.Currency}
                        </p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Payment Status</h6>
                        <p className="fs-16 text-success">Paid</p>
                      </div>
                      <div className="col-lg-3">
                        <h6 className="fs-14 mb-1">Booking Date</h6>
                        <p className="fs-16 text-gray-6">
                          {bookingDetails.BookingDate ? new Date(bookingDetails.BookingDate).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Fare Breakdown */}
                  <div className="pb-4">
                    <h6 className="mb-2">Fare Breakdown</h6>
                    <ul className="list-unstyled small">
                      <li>
                        Room Rate: {rooms?.[0]?.PriceBreakUp?.RoomRate}{" "}
                        {rooms?.[0]?.PriceBreakUp?.CurrencyCode}
                      </li>
                      <li>
                        Service Fee: {rooms?.[0]?.PriceBreakUp?.ServiceFee}{" "}
                        {rooms?.[0]?.PriceBreakUp?.CurrencyCode}
                      </li>
                      <li>
                        Tax: {bookingDetails.NetTax}{" "}
                        {rooms?.[0]?.PriceBreakUp?.CurrencyCode}
                      </li>
                    </ul>
                    <h5 className="text-primary mt-2">
                      Total Paid: {bookingDetails.InvoiceAmount}{" "}
                      {rooms?.[0]?.PriceBreakUp?.CurrencyCode}
                    </h5>
                  </div>

                  {/* Supplements */}
                  {rooms.some((r: any) => r.Supplements?.length > 0) && (
                    <div className="pb-4">
                      <h6 className="mb-2">Extra Services</h6>
                      <ul className="list-unstyled small">
                        {rooms.flatMap((r: any) => r.Supplements || []).map((s: any, i: number) => (
                          <li key={i}>
                            {s.Description || "Supplement"} - {s.Price} {s.Currency}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* / Booking Confirmation */}
        </div>
      </div>
    </div>
  );
};

export default HotelBooking;
